import { useEffect, useState } from 'react';
import { adWriteApi } from 'api/adWriteApi';
import { fireToast } from 'helpers/Toasts';
import { useAdDetailsContext } from 'contexts/AdDetailsContext';
import { useVerificationContext } from 'contexts/VerificationContext';
import { useUserContext } from 'contexts/UserContext';
import { pushToDataLayer } from 'services/gtmService';

import { VerifyTooltip } from 'components/PageWrappers/SharedComponents/VerifyTooltip/VerifyTooltip';
import { Button } from 'components/Toolkit/Button/Button';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { formatAuthorizationHeader } from 'helpers/auth';

interface RevealSendMessageProps {
  adId: number;
  size?: 'SMALL' | 'LARGE' | 'GROW';
  testId?: string;
  trackingId?: string;
  clickSource?: string;
  disabled?: boolean;
}

function RevealSendMessage({
  adId,
  size = 'SMALL',
  testId = 'reveal-send-message',
  trackingId,
  clickSource = '',
  disabled = false,
}: RevealSendMessageProps) {
  const { setIsSendMessageModalOpen, setMessage, message } =
    useAdDetailsContext();
  const { triggerAfterVerify, setTriggerAfterVerify } =
    useVerificationContext();
  const { user, isLoginModalOpen } = useUserContext();
  const { handleLogin } = useAuthOptions();
  const [loginAndSendMessage, setLoginAndSendMessage] =
    useState<boolean>(false);
  const { accessToken } = useAuthOptions();

  useEffect(() => {
    if (!disabled && user && loginAndSendMessage) {
      if (user.isMinimumVerified) {
        handleReveal();
      } else {
        fireToast({
          type: 'INFO',
          text: 'You will need to verify your phone or email before sending a message',
        });
      }
      setLoginAndSendMessage(false);
    }
  }, [isLoginModalOpen]);

  useEffect(() => {
    if (triggerAfterVerify && user && user.isMinimumVerified) {
      setTriggerAfterVerify(false);
      handleReveal();
    }
  }, [triggerAfterVerify]);

  function loginAndSendMessageAction() {
    setLoginAndSendMessage(true);
    handleLogin();
  }

  async function handleReveal() {
    pushToDataLayer({ event: 'message_intent', source: clickSource });
    try {
      if (!message) {
        const {
          data: { message },
        } = await adWriteApi.getSendMessageInitialValue(
          adId,
          formatAuthorizationHeader(accessToken),
        );
        setMessage(message);
      }
      setIsSendMessageModalOpen(true);
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return false;
    }
  }
  return (
    <>
      {user && !user.isMinimumVerified ? (
        <VerifyTooltip
          arrowPosition={['right', 'right', 'center']}
          tooltipPosition={'above'}
          text="You’ll need to verify your email or phone number before you can send enquiries"
        >
          <Button
            disabled={disabled}
            size={size}
            data-testid={testId}
            data-tracking={trackingId}
          >
            Send Message
          </Button>
        </VerifyTooltip>
      ) : (
        <Button
          disabled={disabled}
          size={size}
          onClick={user ? handleReveal : loginAndSendMessageAction}
          data-testid={testId}
          data-tracking={trackingId}
        >
          Send Message
        </Button>
      )}
    </>
  );
}

export { RevealSendMessage };
